import React from 'react';

interface LogoProps {
  className?: string;
  animated?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * NanoLez Educative Logo - Book Stack with Ascending Growth
 * 
 * Design Philosophy:
 * - Book Stack: Core learning foundation with 3 layers
 * - Ascending Arrow: Clear upward skill trajectory
 * - Skill Nodes: Progressive learning checkpoints
 * - Golden-Red Gradient: Premium, aspirational, urgent learning
 * - Light Rays: Enlightenment and knowledge breakthrough
 * 
 * Psychological Mechanisms:
 * - Stacked books: Traditional knowledge, depth, accumulation
 * - Ascending path: Progress, growth, achievement
 * - Glowing effects: Intelligence, breakthrough moments
 * - Dynamic animation: Energy, momentum, active learning
 * - Layered complexity: Progression from beginner to expert
 */
export const Logo: React.FC<LogoProps> = ({ 
  className = '', 
  animated = true,
  size = 'md'
}) => {
  const sizeMap = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <div className={`${sizeMap[size]} ${className}`}>
      <svg
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-lg"
      >
        <defs>
          {/* Premium gradient: Gold to Red */}
          <linearGradient id="premiumGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FBBF24" />
            <stop offset="50%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#DC2626" />
          </linearGradient>

          {/* Sky gradient for background */}
          <linearGradient id="skyGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#FBBF24" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#F97316" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#DC2626" stopOpacity="0.1" />
          </linearGradient>

          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Strong glow filter */}
          <filter id="strongGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Animations */}
          <style>{`
            @keyframes glow-pulse {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
            @keyframes float-up {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-3px); }
            }
            @keyframes shimmer {
              0%, 100% { opacity: 0; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </defs>

        {/* Background - represents learning foundation */}
        <circle cx="60" cy="60" r="58" fill="url(#skyGradient)" />

        {/* Outer ring - knowledge boundary */}
        <circle
          cx="60"
          cy="60"
          r="56"
          fill="none"
          stroke="url(#premiumGradient)"
          strokeWidth="1"
          opacity="0.4"
        />

        {/* Book Stack - Main Element (3 books representing learning layers) */}
        <g>
          {/* Book 1 - Bottom (Foundation) */}
          <rect
            x="35"
            y="70"
            width="50"
            height="12"
            rx="2"
            fill="url(#premiumGradient)"
            opacity="0.7"
            filter="url(#glow)"
          />
          <rect x="35" y="70" width="50" height="2" fill="#FBBF24" opacity="0.5" />

          {/* Book 2 - Middle (Growth) */}
          <rect
            x="38"
            y="55"
            width="44"
            height="12"
            rx="2"
            fill="url(#premiumGradient)"
            opacity="0.85"
            filter="url(#glow)"
            style={animated ? { animation: 'float-up 2.5s ease-in-out infinite 0.1s' } : {}}
          />
          <rect x="38" y="55" width="44" height="2" fill="#F97316" opacity="0.6" />

          {/* Book 3 - Top (Mastery) */}
          <rect
            x="42"
            y="40"
            width="36"
            height="12"
            rx="2"
            fill="url(#premiumGradient)"
            opacity="1"
            filter="url(#strongGlow)"
            style={animated ? { animation: 'float-up 2.5s ease-in-out infinite' } : {}}
          />
          <rect x="42" y="40" width="36" height="2" fill="#DC2626" opacity="0.8" />

          {/* Sparkle light on top book */}
          <circle
            cx="55"
            cy="38"
            r="2"
            fill="#FBBF24"
            opacity="0.9"
            filter="url(#strongGlow)"
            style={animated ? { animation: 'glow-pulse 1.5s ease-in-out infinite' } : {}}
          />
        </g>

        {/* Ascending Arrow - Skill Growth Direction */}
        <g style={animated ? { animation: 'float-up 3s ease-in-out infinite' } : {}}>
          {/* Arrow shaft */}
          <line
            x1="70"
            y1="75"
            x2="82"
            y2="40"
            stroke="url(#premiumGradient)"
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity="0.8"
            filter="url(#glow)"
          />
          
          {/* Arrow head */}
          <polygon
            points="82,40 77,48 85,45"
            fill="url(#premiumGradient)"
            opacity="0.9"
            filter="url(#glow)"
          />
          
          {/* Arrow highlight */}
          <line
            x1="70"
            y1="75"
            x2="82"
            y2="40"
            stroke="#FBBF24"
            strokeWidth="1"
            strokeLinecap="round"
            opacity="0.4"
          />
        </g>

        {/* Skill Nodes on ascending path - Learning Checkpoints */}
        <g>
          {/* Node 1 */}
          <circle
            cx="72"
            cy="65"
            r="2.5"
            fill="#F97316"
            filter="url(#glow)"
            style={animated ? { animation: 'glow-pulse 1.5s ease-in-out infinite 0.2s' } : {}}
          />
          
          {/* Node 2 */}
          <circle
            cx="77"
            cy="55"
            r="2.5"
            fill="#F97316"
            filter="url(#glow)"
            style={animated ? { animation: 'glow-pulse 1.5s ease-in-out infinite 0.4s' } : {}}
          />
          
          {/* Node 3 - Top (Mastery) */}
          <circle
            cx="82"
            cy="45"
            r="3"
            fill="#DC2626"
            filter="url(#strongGlow)"
            style={animated ? { animation: 'glow-pulse 1.5s ease-in-out infinite 0.6s' } : {}}
          />
        </g>

        {/* Light rays suggesting enlightenment/learning */}
        {animated && (
          <g opacity="0.3">
            <line
              x1="42"
              y1="30"
              x2="35"
              y2="20"
              stroke="url(#premiumGradient)"
              strokeWidth="1"
              strokeLinecap="round"
              style={{ animation: 'shimmer 2s ease-in-out infinite' }}
            />
            <line
              x1="60"
              y1="25"
              x2="60"
              y2="12"
              stroke="url(#premiumGradient)"
              strokeWidth="1"
              strokeLinecap="round"
              style={{ animation: 'shimmer 2s ease-in-out infinite 0.3s' }}
            />
            <line
              x1="78"
              y1="30"
              x2="85"
              y2="20"
              stroke="url(#premiumGradient)"
              strokeWidth="1"
              strokeLinecap="round"
              style={{ animation: 'shimmer 2s ease-in-out infinite 0.6s' }}
            />
          </g>
        )}

        {/* Bottom outer circle */}
        <circle
          cx="60"
          cy="60"
          r="58"
          fill="none"
          stroke="url(#premiumGradient)"
          strokeWidth="1.5"
          opacity="0.2"
        />
      </svg>
    </div>
  );
};

export default Logo;
